import { Alert, Badge, Button, Card, Group, Progress, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api';
import { Cabecera, EJERCICIO_ACTUAL, euros, fechaCorta, TRIMESTRE_ACTUAL } from '../comun';
import { useSesion } from '../sesion';
import type { Aviso, Obligacion, Resumen } from '../tipos';

const COLOR_NIVEL = { urgente: 'red', atencion: 'orange', info: 'blue' } as const;

export function PaginaInicio() {
  const s = useSesion();
  const resumen = useQuery({ queryKey: ['resumen', EJERCICIO_ACTUAL], queryFn: () => api.get<Resumen>(`/fiscal/resumen/${EJERCICIO_ACTUAL}`) });
  const avisos = useQuery({ queryKey: ['avisos', 'abiertos'], queryFn: () => api.get<Aviso[]>('/avisos') });
  const calendario = useQuery({
    queryKey: ['calendario', EJERCICIO_ACTUAL],
    queryFn: async () => {
      const [a, b] = await Promise.all([
        api.get<{ obligaciones: Obligacion[] }>(`/fiscal/calendario/${EJERCICIO_ACTUAL - 1}`),
        api.get<{ obligaciones: Obligacion[] }>(`/fiscal/calendario/${EJERCICIO_ACTUAL}`),
      ]);
      return [...a.obligaciones, ...b.obligaciones];
    },
  });
  const t = resumen.data?.trimestres[TRIMESTRE_ACTUAL - 1];
  const proximos = (calendario.data ?? [])
    .filter((o) => !o.condicional && o.estado !== 'presentada')
    .sort((a, b) => a.hasta.localeCompare(b.hasta))
    .slice(0, 4);

  return (
    <>
      <Cabecera titulo={`Hola, ${s.usuario?.nombre.split(' ')[0] ?? ''}`} descripcion={`${s.negocio?.nombre} · ${TRIMESTRE_ACTUAL}T ${EJERCICIO_ACTUAL}`}>
        <Button component={Link} to="/ingresos" variant="light">
          Anotar ventas
        </Button>
        <Button component={Link} to="/gastos" variant="light">
          Anotar gasto
        </Button>
      </Cabecera>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
        <Tarjeta titulo={`Ingresos ${TRIMESTRE_ACTUAL}T`} valor={euros(t?.ingresos)} detalle={`${t?.numIngresos ?? 0} movimientos`} />
        <Tarjeta titulo={`Gastos deducibles ${TRIMESTRE_ACTUAL}T`} valor={euros(t?.gastos)} detalle={`${t?.numGastos ?? 0} movimientos`} />
        <Tarjeta titulo={`Rendimiento ${EJERCICIO_ACTUAL}`} valor={euros(resumen.data?.anual.rendimiento)} detalle={`Ingresos ${euros(resumen.data?.anual.ingresos)} · Gastos ${euros(resumen.data?.anual.gastos)}`} />
      </SimpleGrid>

      {!s.negocio?.esDemo && s.usuario && !s.usuario.totpActivo && (
        <Alert color="orange" mb="md" icon={<IconAlertTriangle size={18} />} title="Protege tu cuenta">
          Estos datos son fiscales. Activa la <Link to="/ajustes">verificación en dos pasos</Link> (Google Authenticator o Contraseñas de Apple) y vincula <b>Telegram</b> para recibir los avisos en el móvil.
        </Alert>
      )}

      {resumen.data?.anual.gastosPendientes ? (
        <Alert color="orange" mb="md" icon={<IconAlertTriangle size={18} />}>
          Tienes {resumen.data.anual.gastosPendientes} gasto(s) pendientes de justificar.{' '}
          <Link to="/gastos">Adjunta sus facturas</Link> para que cuenten.
        </Alert>
      ) : null}

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Avisos</Text>
            <Button component={Link} to="/avisos" variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />}>
              Ver todos
            </Button>
          </Group>
          <Stack gap="xs">
            {(avisos.data ?? []).slice(0, 5).map((a) => (
              <Group key={a.id} gap="xs" wrap="nowrap" align="flex-start">
                <Badge color={COLOR_NIVEL[a.nivel]} size="xs" mt={4} style={{ flexShrink: 0 }}>
                  {{ urgente: 'Urgente', atencion: 'Atención', info: 'Info' }[a.nivel]}
                </Badge>
                {a.enlace ? (
                  <Text size="sm" component={Link} to={a.enlace} style={{ textDecoration: 'none', color: 'inherit' }}>
                    {a.titulo}
                  </Text>
                ) : (
                  <Text size="sm">{a.titulo}</Text>
                )}
              </Group>
            ))}
            {avisos.data?.length === 0 && (
              <Text size="sm" c="dimmed">
                No hay avisos pendientes.
              </Text>
            )}
          </Stack>
        </Card>

        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Próximos plazos</Text>
            <Button component={Link} to="/fiscal" variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />}>
              Impuestos
            </Button>
          </Group>
          <Stack gap="xs">
            {proximos.map((o) => (
              <Group key={o.clave} justify="space-between" wrap="nowrap">
                <Text size="sm">{o.titulo}</Text>
                <Badge variant="light" style={{ flexShrink: 0 }} color={o.estado === 'vencida' ? 'red' : o.estado === 'en_plazo' ? 'orange' : 'gray'}>
                  {o.estado === 'vencida' ? 'vencido' : 'hasta'} {fechaCorta(o.hasta)}
                </Badge>
              </Group>
            ))}
          </Stack>
        </Card>

        {resumen.data?.repep && (
          <Card withBorder>
            <Text fw={600} mb={4}>
              Límite del REPEP (IGIC)
            </Text>
            <Text size="sm" c="dimmed" mb="xs">
              Volumen {EJERCICIO_ACTUAL}: {euros(resumen.data.repep.volumenAnualizado)} de {euros(resumen.data.repep.umbralSiguiente)} (límite para seguir en el REPEP en {EJERCICIO_ACTUAL + 1})
            </Text>
            <Progress value={Math.min(resumen.data.repep.porcentajeUmbral / 100, 100)} color={resumen.data.repep.porcentajeUmbral >= 8000 ? 'orange' : 'teal'} />
          </Card>
        )}
        {resumen.data && (
          <Card withBorder>
            <Text fw={600} mb={4}>
              Indicador RETA (informativo)
            </Text>
            <Text size="sm" c="dimmed" mb="xs">
              Rendimiento neto {euros(resumen.data.indicadorReta.rendimientoNeto)} frente al SMI anual {euros(resumen.data.indicadorReta.smiAnual)}
            </Text>
            <Progress value={Math.min(resumen.data.indicadorReta.porcentaje / 100, 100)} color={resumen.data.indicadorReta.porcentaje >= 8000 ? 'orange' : 'teal'} />
            <Text size="xs" c="dimmed" mt="xs">
              {resumen.data.indicadorReta.nota}
            </Text>
          </Card>
        )}
      </SimpleGrid>
    </>
  );
}

function Tarjeta({ titulo, valor, detalle }: { titulo: string; valor: string; detalle: string }) {
  return (
    <Card withBorder>
      <Text size="sm" c="dimmed">
        {titulo}
      </Text>
      <Text fw={700} fz={26}>
        {valor}
      </Text>
      <Text size="xs" c="dimmed">
        {detalle}
      </Text>
    </Card>
  );
}
